// frontend/src/components/SIORecommendations.jsx
//
// SIO recommendation gallery — the entry experience of Step 2.
//
// Shows up to six named, server-priced itinerary blueprints
// (Best Wildlife / Migration / Budget / Family / Balanced / Luxury).
// One tap applies parks, night split, migration zones, and lodges to the
// builder; "Design from scratch" drops straight into the manual flow.
//
// Signature element: the NIGHT-ALLOCATION BAR — the trip rendered as
// blocks sized proportionally to nights per park, zone-coloured for
// Serengeti. Night splitting is what the optimizer actually decides,
// so the bar encodes real information, not decoration.
//
// Exports:
//   <SIORecommendations …/>   full gallery (loading / error / cards)
//   <SIOAppliedBanner …/>     compact strip shown after a card is applied

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  Sparkles, Check, ChevronDown, MapPin, Loader2,
  ArrowRight, Info, Compass, RefreshCw,
} from 'lucide-react';
import { RECOMMEND_ITINERARIES } from '../graphql/sio';

/* ─── Design tokens (shared with TourBuilderPage) ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

/* One palette — the site's gold and navy. Categories differ by their
   words, not by a colour each (green / burgundy / blue read as another
   site). */
const CATEGORY_META = {
  wildlife:  { tagline: 'Premier game viewing'      },
  migration: { tagline: 'Follow the herds'          },
  budget:    { tagline: 'Most safari for the money' },
  luxury:    { tagline: 'The finest camps'          },
  family:    { tagline: 'Gentle pace, short drives' },
  balanced:  { tagline: 'The all-rounder'           },
};

/* Serengeti zone colours — mirrors ZONE_UI_META in StepParksLodgesExperiences */
const ZONE_COLORS = {
  southern: '#c9a46a',
  western:  '#8f7445',
  northern: '#a8553a',
  central:  GOLD,
};

const fmtMoney = (n, cur = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: cur, maximumFractionDigits: 0,
  }).format(n || 0);

/* Short park name for tight spaces: "Ngorongoro Conservation Area" → "Ngorongoro" */
const shortPark = (name = '') =>
  name.replace(/\s+(National Park|Conservation Area|National Reserve|Game Reserve)$/i, '');

const LOADING_LINES = [
  'Scoring hundreds of itinerary shapes…',
  'Matching your dates to migration zones…',
  'Allocating nights between parks…',
  'Selecting bookable lodges…',
  'Pricing through the TANAPA fee schedule…',
];

const Styles = () => (
  <style>{`
    @keyframes sioFadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:none; } }
    @keyframes sioShimmer { 0% { background-position:200% 0; } 100% { background-position:-200% 0; } }
    @keyframes sioSpin { from { transform:rotate(0); } to { transform:rotate(360deg); } }
    .sio-card { animation: sioFadeUp .5s cubic-bezier(.22,1,.36,1) both; }
    .sio-grid > *:nth-child(2) { animation-delay: 70ms; }
    .sio-grid > *:nth-child(3) { animation-delay: 140ms; }
    .sio-grid > *:nth-child(4) { animation-delay: 210ms; }
    .sio-grid > *:nth-child(5) { animation-delay: 280ms; }
    .sio-grid > *:nth-child(6) { animation-delay: 350ms; }
    @media (prefers-reduced-motion: reduce) {
      .sio-card { animation: none; }
    }
    .sio-skel {
      background: linear-gradient(90deg, rgba(255,255,255,.05) 25%, rgba(255,255,255,.12) 50%, rgba(255,255,255,.05) 75%);
      background-size: 200% 100%;
      animation: sioShimmer 1.4s infinite;
    }
    .sio-use-btn {
      position: relative; overflow: hidden;
      display: flex; align-items: center; justify-content: center; gap: 8px;
      width: 100%; padding: 13px 16px;
      font-family: 'Jost', sans-serif; font-size:13px; font-weight: 600;
      letter-spacing:0.16em; text-transform: uppercase;
      background: ${NAVY}; color: #fff; border: 1px solid ${NAVY};
      cursor: pointer; transition: all .3s cubic-bezier(.22,1,.36,1);
    }
    .sio-use-btn::before {
      content: ''; position: absolute; inset: 0; background: ${GOLD};
      transform: scaleX(0); transform-origin: left;
      transition: transform .3s cubic-bezier(.22,1,.36,1); z-index: 0;
    }
    .sio-use-btn:hover::before { transform: scaleX(1); }
    .sio-use-btn:hover { color: ${NAVY}; border-color: ${GOLD}; }
    .sio-use-btn span, .sio-use-btn svg { position: relative; z-index: 1; }
    .sio-use-btn:focus-visible { outline: 2px solid ${GOLD}; outline-offset: 2px; }

    .sio-card { display:flex; flex-direction:column; background:#fff; border:1px solid #e2ddd6; transition:border-color .3s, box-shadow .3s; }
    .sio-card:hover { border-color: rgba(184,151,90,.45); }
    .sio-card.on { border-color:${GOLD}; box-shadow:0 12px 40px rgba(184,151,90,.18); }
    .sio-head { background:${NAVY}; color:#fff; padding:20px 22px; display:flex; justify-content:space-between; gap:16px; position:relative; }
    .sio-head::after { content:''; position:absolute; left:0; right:0; bottom:0; height:2px; background:linear-gradient(90deg, ${GOLD}, rgba(184,151,90,0)); }
    .sio-eyebrow { display:flex; align-items:center; gap:8px; flex-wrap:wrap; font-family:'Jost',sans-serif; font-size:12.5px; font-weight:600; letter-spacing:.18em; text-transform:uppercase; color:${GOLD}; margin:0 0 6px; }
    .sio-applied { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border:1px solid rgba(184,151,90,.5); color:${GOLD}; letter-spacing:.12em; }
    .sio-route { font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:400; line-height:1.1; margin:0 0 6px; color:#fff; }
    .sio-sub { font-family:'Jost',sans-serif; font-size:13.5px; font-weight:300; color:rgba(255,255,255,.6); margin:0; }
    .sio-price { text-align:right; flex-shrink:0; display:flex; flex-direction:column; align-items:flex-end; }
    .sio-est { font-family:'Jost',sans-serif; font-size:11px; font-weight:600; letter-spacing:.16em; text-transform:uppercase; padding:2px 7px; border:1px solid rgba(184,151,90,.5); color:${GOLD}; margin-bottom:6px; }
    .sio-price-pp { font-family:'Cormorant Garamond',serif; font-size:34px; font-weight:300; line-height:1; color:#fff; }
    .sio-price-lbl { font-family:'Jost',sans-serif; font-size:12px; letter-spacing:.14em; text-transform:uppercase; color:rgba(255,255,255,.55); margin-top:3px; }
    .sio-price-total { font-family:'Jost',sans-serif; font-size:13px; color:rgba(255,255,255,.7); margin-top:6px; }
    .sio-body { padding:18px 22px 20px; display:flex; flex-direction:column; gap:14px; flex:1; }
    .sio-bar { display:flex; gap:3px; }
    .sio-bar-seg { min-width:64px; background:#faf6ee; border-top:3px solid ${GOLD}; padding:8px 10px; overflow:hidden; display:flex; flex-direction:column; gap:2px; }
    .sio-bar-park { font-family:'Jost',sans-serif; font-size:13px; font-weight:600; color:#12100e; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .sio-bar-n { font-family:'Jost',sans-serif; font-size:12.5px; color:#6b6357; }
    .sio-note { font-family:'Jost',sans-serif; font-size:13.5px; line-height:1.6; color:#3d3326; margin:0; padding:10px 12px; background:#faf8f4; border-left:2px solid ${GOLD}; }
    .sio-note.warn { background:#fbf6ea; }
    .sio-reasons { list-style:none; margin:0; padding:0; display:grid; gap:7px; }
    .sio-reasons li { position:relative; padding-left:18px; font-family:'Jost',sans-serif; font-size:14.5px; font-weight:300; line-height:1.55; color:#12100e; }
    .sio-reasons li::before { content:''; position:absolute; left:2px; top:8px; width:7px; height:7px; border:1px solid ${GOLD}; transform:rotate(45deg); }
    .sio-meta { display:flex; flex-wrap:wrap; align-items:center; gap:14px; padding-top:12px; border-top:1px solid #f0ece6; font-family:'Jost',sans-serif; font-size:13.5px; color:#3d3326; }
    .sio-meta > span { display:inline-flex; align-items:center; gap:6px; }
    .sio-meta svg { color:${GOLD}; }
    .sio-warn { color:#8a5a1c !important; cursor:help; }
    .sio-toggle { margin-left:auto; display:inline-flex; align-items:center; gap:5px; background:none; border:none; cursor:pointer; font-family:'Jost',sans-serif; font-size:12.5px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; color:#12100e; }
    .sio-days { border:1px solid #f0ece6; background:#fcfbf8; }
    .sio-day { padding:11px 14px; border-bottom:1px solid #f0ece6; }
    .sio-day:last-of-type { border-bottom:none; }
    .sio-day-park { font-family:'Cormorant Garamond',serif; font-size:19px; color:${NAVY}; }
    .sio-day-n { font-family:'Jost',sans-serif; font-size:12px; letter-spacing:.14em; text-transform:uppercase; color:#6b6357; white-space:nowrap; }
    .sio-day-lodge { font-family:'Jost',sans-serif; font-size:13.5px; color:#3d3326; margin:2px 0 0; }
    .sio-notes { margin:0; padding:10px 14px 12px 30px; border-top:1px solid #f0ece6; font-family:'Jost',sans-serif; font-size:13px; color:#8a5a1c; display:grid; gap:4px; }
    @media (max-width:520px) { .sio-head { flex-direction:column; } .sio-price { align-items:flex-start; text-align:left; } }
  `}</style>
);

/* ═══════════════════════════════════════════════════════════════════
   NIGHT-ALLOCATION BAR — the trip as blocks sized by nights.
═══════════════════════════════════════════════════════════════════ */
const NightBar = ({ stays }) => {
  const total = stays.reduce((s, st) => s + (st.nights || 1), 0) || 1;
  return (
    <div className="sio-bar">
      {stays.map((st, i) => {
        const zone = st.migrationZones?.[0];
        const color = zone ? (ZONE_COLORS[zone] || GOLD) : GOLD;
        return (
          <div key={`${st.parkId}-${i}`} className="sio-bar-seg"
            title={`${st.parkName} · ${st.nights} night${st.nights !== 1 ? 's' : ''}${st.propertyName ? ` · ${st.propertyName}` : ' · lodge to be confirmed'}`}
            style={{ flex: `${(st.nights || 1) / total} 1 0`, borderTopColor: color }}>
            <span className="sio-bar-park">{shortPark(st.parkName)}</span>
            <span className="sio-bar-n">{st.nights} night{st.nights !== 1 ? 's' : ''}</span>
          </div>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   RECOMMENDATION CARD — the site's editorial style: navy head, gold
   eyebrow, serif route; the price says what it is and what it covers.
═══════════════════════════════════════════════════════════════════ */
const RecommendationCard = ({ rec, isApplied, onApply, guests }) => {
  const [open, setOpen] = useState(false);
  const meta = CATEGORY_META[rec.category] || CATEGORY_META.balanced;
  const cur  = rec.currency || 'USD';
  const stays = rec.stays || [];
  const route = stays.map((s) => shortPark(s.parkName)).join(' → ');
  const countries = [...new Set(stays.map((s) => s.parkCountrySlug).filter(Boolean))]
    .map((c) => c.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()));
  const lodges = stays.filter((s) => s.propertyName).length;
  const estimate = rec.isPriced && rec.nightsWithoutLodge > 0;

  const dayGroups = useMemo(() => {
    const groups = [];
    (rec.days || []).forEach((d) => {
      const last = groups[groups.length - 1];
      if (last && last.parkId === d.parkId) last.days.push(d);
      else groups.push({ parkId: d.parkId, park: d.park, lodge: d.accommodationName, days: [d] });
    });
    return groups;
  }, [rec.days]);

  return (
    <article className={`sio-card${isApplied ? ' on' : ''}`}>
      <header className="sio-head">
        <div style={{ minWidth: 0 }}>
          <p className="sio-eyebrow">
            {rec.categoryLabel}
            {isApplied && <span className="sio-applied"><Check size={11} strokeWidth={2.6} /> Chosen</span>}
          </p>
          <h3 className="sio-route">{route}</h3>
          <p className="sio-sub">{meta.tagline}{countries.length ? ` · ${countries.join(' & ')}` : ''}</p>
        </div>
        <div className="sio-price">
          {rec.isPriced ? (
            <>
              {estimate && <span className="sio-est">Estimate</span>}
              <span className="sio-price-pp">{fmtMoney(rec.pricePerPerson, cur)}</span>
              <span className="sio-price-lbl">per person</span>
              <span className="sio-price-total">{fmtMoney(rec.totalPrice, cur)} total{guests ? ` · ${guests} guest${guests !== 1 ? 's' : ''}` : ''}</span>
            </>
          ) : (
            <span className="sio-price-lbl" style={{ maxWidth: 150, display: 'block' }}>Priced by our team</span>
          )}
        </div>
      </header>

      <div className="sio-body">
        <NightBar stays={stays} />

        {rec.priceNote && <p className={`sio-note${estimate || !rec.isPriced ? ' warn' : ''}`}>{rec.priceNote}</p>}

        {/* The route visits a migration park but not where the herds are on
            these dates — said plainly, never left to the reader. */}
        {rec.migrationInSeason === false && rec.migrationNote && (
          <p className="sio-note warn"><strong style={{ fontWeight: 600 }}>Great Migration: </strong>{rec.migrationNote}</p>
        )}

        {(rec.reasons || []).length > 0 && (
          <ul className="sio-reasons">
            {rec.reasons.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}

        <div className="sio-meta">
          <span><Compass size={13} strokeWidth={1.5} /> {Math.round(rec.totalDistanceKm || 0).toLocaleString()} km by road</span>
          <span><MapPin size={13} strokeWidth={1.5} /> {lodges} of {stays.length} stay{stays.length !== 1 ? 's' : ''} with a lodge</span>
          {(rec.warnings || []).length > 0 && (
            <span className="sio-warn" title={rec.warnings.join('\n')}><Info size={13} strokeWidth={1.6} /> {rec.warnings.length} note{rec.warnings.length !== 1 ? 's' : ''}</span>
          )}
          <button type="button" className="sio-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            Day by day <ChevronDown size={13} strokeWidth={1.6} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .25s' }} />
          </button>
        </div>

        {open && (
          <div className="sio-days">
            {dayGroups.map((g, gi) => (
              <div key={gi} className="sio-day">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
                  <span className="sio-day-park">{g.park}</span>
                  <span className="sio-day-n">Day {g.days[0].dayNumber}{g.days.length > 1 ? `–${g.days[g.days.length - 1].dayNumber}` : ''}</span>
                </div>
                <p className="sio-day-lodge">
                  {g.lodge ? `Stay at ${g.lodge}` : 'Lodge to be confirmed by our team'}
                  {g.days[0].migrationZone ? ` · ${g.days[0].migrationZone} migration zone` : ''}
                </p>
              </div>
            ))}
            {(rec.warnings || []).length > 0 && (
              <ul className="sio-notes">{rec.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            )}
          </div>
        )}

        <button className="sio-use-btn" onClick={() => onApply(rec)}>
          <span>{isApplied ? 'Chosen — adjust it below' : 'Start from this plan'}</span>
          {isApplied ? <Check size={13} strokeWidth={2.5} /> : <ArrowRight size={13} />}
        </button>
      </div>
    </article>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   MAIN GALLERY
═══════════════════════════════════════════════════════════════════ */
const SIORecommendations = ({
  countrySlugs,
  touristComposition,
  startDate,            // 'YYYY-MM-DD'
  endDate,              // 'YYYY-MM-DD'
  ready = true,         // nationality set etc.
  appliedCategory = null,
  onApply,              // (rec) => void
  onSkip,               // () => void — “Design from scratch”
}) => {
  const [loadingLine, setLoadingLine] = useState(0);

  const { data, loading, error, refetch } = useQuery(RECOMMEND_ITINERARIES, {
    variables: {
      countrySlugs,
      touristComposition,
      startDate,
      endDate,
      maxRecommendations: 6,
      includePricing: true,
    },
    skip: !ready || !startDate || !endDate || !countrySlugs?.length,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setLoadingLine(l => (l + 1) % LOADING_LINES.length), 1700);
    return () => clearInterval(t);
  }, [loading]);

  const recs = data?.recommendItineraries || [];

  return (
    <div>
      <Styles />

      {/* Section heading */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <div style={{ width: '24px', height: '1px', background: GOLD }} />
          <span style={{ fontFamily: "'Jost', sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD }}>
            Step 02 · Suggested routes
          </span>
        </div>
        <h2 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(32px,3.5vw,40px)', fontStyle: 'normal',
          fontWeight: 300, color: '#0f172a', lineHeight: 1, marginBottom: '8px',
        }}>
          {recs.length > 1 ? `${['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][recs.length] || recs.length} ways to take this safari` : 'Ways to take this safari'}
        </h2>
        <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '15px', fontWeight: 300, color: '#12100e', maxWidth: '520px', lineHeight: 1.6 }}>
          Each route is planned for your dates and group — parks, nights, migration
          zones and lodges — with an estimated price. Start from one and change
          anything, or plan from a blank page.
        </p>
      </div>

      {/* Not ready */}
      {!ready && (
        <div style={{ border: '1px solid #e2ddd6', background: '#fff', padding: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Info size={13} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
          <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '15px', fontWeight: 300, color: '#12100e' }}>
            Set each traveller's nationality in Step 1 — routes are priced for
            your group, and park fees differ by nationality.
          </p>
        </div>
      )}

      {/* Loading */}
      {ready && loading && (
        <div style={{ border: '1px solid rgba(184,151,90,0.2)', background: NAVY, padding: '36px 28px', textAlign: 'center' }}>
          <Loader2 size={26} strokeWidth={1.5} style={{ color: GOLD, animation: 'sioSpin 1s linear infinite', margin: '0 auto 14px' }} />
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '25px', fontStyle: 'normal', fontWeight: 300, color: 'rgba(255,255,255,0.85)', marginBottom: '6px' }}>
            Planning your routes
          </p>
          <p aria-live="polite" style={{ fontFamily: "'Jost', sans-serif", fontSize: '13px', fontWeight: 300, color: 'rgba(255,255,255,0.4)', minHeight: '15px' }}>
            {LOADING_LINES[loadingLine]}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px', marginTop: '24px' }}>
            {[1, 2, 3].map(i => <div key={i} className="sio-skel" style={{ height: '120px' }} />)}
          </div>
        </div>
      )}

      {/* Error */}
      {ready && !loading && error && (
        <div style={{ border: '1px solid #e2ddd6', background: '#fff', padding: '24px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' }}>
            <Info size={15} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0, marginTop: '3px' }} />
            <div>
              {/* The server's own message says why and what to do next. */}
              <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '15px', color: '#12100e', lineHeight: 1.6 }}>
                {(error.message || '').replace(/^.*?:\s*(?=We )/, '') || "We couldn't suggest routes for these dates — plan it below and our team will help."}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => refetch()} style={{
              display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '10px 18px',
              border: `1px solid ${GOLD}`, background: GOLD_DIM, color: NAVY,
              fontFamily: "'Jost', sans-serif", fontSize: '13px', fontWeight: 600,
              letterSpacing: '0.16em', textTransform: 'uppercase', cursor: 'pointer',
            }}>
              <RefreshCw size={10} strokeWidth={1.5} /> Try again
            </button>
            <button onClick={onSkip} style={{
              padding: '10px 18px', border: '1px solid #ddd9d2', background: '#fff', color: '#12100e',
              fontFamily: "'Jost', sans-serif", fontSize: '13px', fontWeight: 600,
              letterSpacing: '0.16em', textTransform: 'uppercase', cursor: 'pointer',
            }}>
              Design from scratch
            </button>
          </div>
        </div>
      )}

      {/* Cards */}
      {ready && !loading && !error && recs.length > 0 && (
        <>
          <div className="sio-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))',
            gap: '16px',
          }}>
            {recs.map(rec => (
              <RecommendationCard
                key={rec.category}
                rec={rec}
                isApplied={appliedCategory === rec.category}
                onApply={onApply}
                guests={(touristComposition || []).filter((t) => (t.touristType || '').toLowerCase() !== 'infant').length}
              />
            ))}
          </div>

          {/* Escape hatch */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '28px' }}>
            <button onClick={onSkip} style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '14px 28px', border: `1px solid ${GOLD}`,
              background: GOLD_DIM, color: NAVY,
              fontFamily: "'Jost', sans-serif", fontSize: '13px', fontWeight: 600,
              letterSpacing: '0.16em', textTransform: 'uppercase', cursor: 'pointer',
              transition: 'all .25s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = GOLD_DIM; e.currentTarget.style.color = NAVY; }}>
              <Sparkles size={12} strokeWidth={1.5} />
              Plan from a blank page instead
            </button>
          </div>
        </>
      )}

      {/* Empty */}
      {ready && !loading && !error && recs.length === 0 && (
        <div style={{ border: '1px solid #e2ddd6', background: '#fff', padding: '24px', textAlign: 'center' }}>
          <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '15px', color: '#12100e', marginBottom: '12px' }}>
            No suggested routes for these destinations yet — plan your safari below and our team will help.
          </p>
          <button onClick={onSkip} className="sio-use-btn" style={{ width: 'auto', margin: '0 auto', padding: '12px 24px' }}>
            <span>Start designing</span><ArrowRight size={11} />
          </button>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   APPLIED BANNER — compact strip shown above the customization UI
   once a blueprint has been applied.
═══════════════════════════════════════════════════════════════════ */
export const SIOAppliedBanner = ({ recommendation, onChangeBlueprint, onClear }) => {
  if (!recommendation) return null;
  const accent = GOLD;
  return (
    <div style={{
      border: `1px solid ${accent}40`, background: `${accent}08`,
      padding: '14px 18px', display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <Sparkles size={12} strokeWidth={1.5} style={{ color: accent, flexShrink: 0 }} />
        <div style={{ minWidth: 0 }}>
          <p style={{ fontFamily: "'Jost', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: accent, marginBottom: '2px' }}>
            {recommendation.categoryLabel} · your starting plan
          </p>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontStyle: 'normal', fontWeight: 300, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {recommendation.title}
            {recommendation.isPriced && (
              <span style={{ fontFamily: "'Jost', sans-serif", fontSize: '13px', fontStyle: 'normal', color: '#12100e' }}>
                {'  '}· {recommendation.nightsWithoutLodge > 0 ? 'est. ' : ''}{fmtMoney(recommendation.pricePerPerson, recommendation.currency)} per person
              </span>
            )}
          </p>
        </div>
      </div>
      {/* On the guest's dates, does this plan meet the herds? */}
      {recommendation.migrationNote && recommendation.migrationInSeason != null && (
        <p style={{
          flexBasis: '100%', margin: 0, fontFamily: "'Jost', sans-serif", fontSize: '13.5px', lineHeight: 1.6,
          color: recommendation.migrationInSeason ? '#3d3326' : '#8a3d2f',
          padding: '8px 10px', background: recommendation.migrationInSeason ? 'rgba(184,151,90,.08)' : 'rgba(180,90,74,.06)',
          borderLeft: `2px solid ${recommendation.migrationInSeason ? GOLD : '#b45a4a'}`,
        }}>
          <strong style={{ fontWeight: 600 }}>
            {recommendation.migrationInSeason ? '✓ Meets the Great Migration on your dates. ' : '✗ Misses the Great Migration on your dates. '}
          </strong>
          {recommendation.migrationNote}
        </p>
      )}
      <div style={{ display: 'flex', gap: '14px', flexShrink: 0 }}>
        <button onClick={onChangeBlueprint} style={{
          fontFamily: "'Jost', sans-serif", fontSize: '12px', fontWeight: 600,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          background: 'none', border: 'none', color: accent, cursor: 'pointer',
        }}>
          Choose another plan
        </button>
        <button onClick={onClear} style={{
          fontFamily: "'Jost', sans-serif", fontSize: '12px', fontWeight: 600,
          letterSpacing: '0.14em', textTransform: 'uppercase',
          background: 'none', border: 'none', color: '#12100e', cursor: 'pointer',
        }}>
          Clear
        </button>
      </div>
    </div>
  );
};

export default SIORecommendations;